import { Injectable, NgZone } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ActionPerformed, PushNotifications } from '@capacitor/push-notifications';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { UserService } from './user-service.service';
import { AppService } from './app.service';
import { UserProfileService } from './user-profile.service';
import { User } from '../app/models/user.model';
import { Router } from '@angular/router';
import { HybridStorageKeys, HybridStorageService } from '../app/services/hybrid-storage.service';

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private tokenSubject = new BehaviorSubject<string | null>(null);
  token$ = this.tokenSubject.asObservable();
  private isLoggedIn = false;
  private systemPushPermissionGranted = false;
  private userId: number | null = null;
  
  constructor(
    private userService: UserService,
    private appService: AppService,
    private userProfileService: UserProfileService,
    private hybridStorageService: HybridStorageService,
    private router: Router,
    private zone: NgZone
  ) {
    if (Capacitor.isNativePlatform()) {
      PushNotifications.addListener('registration', async (token) => {
        let deviceInfo: any = await this.userProfileService.getUserCurrentMobileDeviceInformation();
        await this.saveTokenToBackend(
          token.value,
          deviceInfo.deviceId,
          deviceInfo.deviceModelVersion,
          deviceInfo.devicePlatform,
          deviceInfo.deviceOSVersion,
          deviceInfo.deviceManufacturerVersion
        );
        this.tokenSubject.next(token.value);
      });   

      // the registration event should be called after the register() method          
      
      // event listener for push notifications
      // Handle registration errors
      PushNotifications.addListener('registrationError', (error) => {
        console.error('Push Registration Failed:', error);
      });

      // Handle push notification tap action
      PushNotifications.addListener('pushNotificationActionPerformed', async (action: ActionPerformed) => {
        const notificationData = action.notification.data;
        if (notificationData && notificationData.deeplink) {
          // check if the user is authenticated
          let isUserLoggedIn = await this.userService.isUserAuthenticated();

          if (isUserLoggedIn) {
            const currentUrl = this.router.url;
            const isAtAuthRoute = currentUrl === '/' || currentUrl === '' || currentUrl.includes('/login') || currentUrl.includes('/callback');
            if (isAtAuthRoute) {
              // Cold start in progress - store deeplink for the cold start auth flow to consume
              this.hybridStorageService.setItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath, notificationData.deeplink);
            } else {
              // Warm start / app resume - navigate directly. Plugin callbacks arrive outside
              // Angular's zone, so the navigation must be run inside it or change detection
              // never ticks and the new view does not render (same reason app.component.ts
              // wraps its deeplink navigation).
              this.zone.run(() => this.router.navigateByUrl(notificationData.deeplink));
            }
          } else {
            // store the deeplink in local storage
            this.hybridStorageService.setItem<string>(HybridStorageKeys.Capacitor_DeepLinkPath, notificationData.deeplink);
          }
        }
      });
      // end push notification event set up

      this.userService.authStatus$.subscribe(async (isAuthenticated: boolean) => {
        this.isLoggedIn = isAuthenticated;
        if (isAuthenticated) {
          this.userService.setUserProfile();
          this.userService.currentUserProfile$.subscribe((userProfile) => {
            this.userId = userProfile?.Id ?? null;
          });
          await this.checkSystemPushPermission();
          App.addListener('appStateChange', async (state) => {
            if (state.isActive) {
              await this.checkSystemPushPermission();
            }
          });
        }
      });
    } else {          
      this.appService.setSystemPushPermissionGranted = true;
    }
  }

  async registerPushNotifications(): Promise<boolean> {
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive === 'granted') {
      await PushNotifications.register();
      // the registration event should be called after the register() method          
      return true;
    } else {
      console.warn('Push notification permission denied');
      return false;
    }
  }

  public async unregisterPushNotifications() {
    await PushNotifications.removeAllListeners();
    await PushNotifications.unregister();
  }

  private async checkSystemPushPermission() {
    const permStatus = await PushNotifications.checkPermissions();
    this.systemPushPermissionGranted = permStatus.receive === 'granted';
    this.appService.setSystemPushPermissionGranted = this.systemPushPermissionGranted;

    if (this.systemPushPermissionGranted) {
      const userProfile = await this.fetchUserPreferences();
      if (userProfile?.NotificationPrefs && (userProfile.NotificationPrefs.NotifyMessageByPush || userProfile.NotificationPrefs.NotifyPostByPush)) {
        await this.registerPushNotifications();
      }
    } else {
      console.warn('System push notifications are disabled.');
    }
  }

  private async saveTokenToBackend(token: string, deviceId: string, deviceModel: string, devicePlatform: string, deviceOSVersion: string, deviceManufacturer: string) {
    if (this.isLoggedIn && this.userId) {
      this.userProfileService.savePushNotificationToken(this.userId, token, deviceId, deviceModel, devicePlatform, deviceOSVersion, deviceManufacturer).subscribe((_) => {
      });
    }
  }

  async fetchUserPreferences(): Promise<User | null> {
    if (this.userId !== null) {
      return await lastValueFrom(this.userProfileService.getUserProfileByUserId(this.userId));
    }
    return null;
  }
}
